import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import {
  getFirestore,
  doc,
  collection,
  setDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore';
import type { DraftState, ArchivedDraft } from './types';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyD-AHLn8b4RsfhOvgcti__jXELedx6AjqQ',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'annual-auction-nfl-draft-608a4.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'annual-auction-nfl-draft-608a4',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'annual-auction-nfl-draft-608a4.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '888376909907',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:888376909907:web:0fc49b868be0179d5626a6',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-9ZNNJ4RC4Y',
};

export const APP_ID = firebaseConfig.projectId;
export const DRAFT_YEAR = Number(import.meta.env.VITE_DRAFT_YEAR ?? new Date().getFullYear());
export const COMMISSIONER_IDS = (import.meta.env.VITE_COMMISSIONER_IDS ?? 'rob')
  .split(',')
  .map((s: string) => s.trim().toLowerCase())
  .filter(Boolean);

export const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
export const db: Firestore = getFirestore(firebaseApp);

/**
 * Multi-league support.
 *
 * The original app stored a single draft at
 *   artifacts/{appId}/public/data/footballDraft/draftStatus
 * That data is preserved by treating the league id "default" as a special case
 * that still reads/writes the legacy path. Any other league id lives under
 *   artifacts/{appId}/public/data/leagues/{leagueId}/state/current
 *   artifacts/{appId}/public/data/leagues/{leagueId}/archives/{year}
 *   artifacts/{appId}/public/data/leagues/{leagueId}/meta/info
 */
export const DEFAULT_LEAGUE_ID = 'default';

// Firestore paths
const root = (appId: string) => `artifacts/${appId}/public/data`;
const leagueRoot = (leagueId: string) => `${root(APP_ID)}/leagues/${leagueId}`;
const isLegacy = (leagueId: string) => leagueId === DEFAULT_LEAGUE_ID;

export const draftRef = (leagueId: string = DEFAULT_LEAGUE_ID): DocumentReference =>
  isLegacy(leagueId)
    ? doc(collection(db, `${root(APP_ID)}/footballDraft`), 'draftStatus')
    : doc(collection(db, `${leagueRoot(leagueId)}/state`), 'current');

export const archiveRef = (year: number, leagueId: string = DEFAULT_LEAGUE_ID): DocumentReference =>
  isLegacy(leagueId)
    ? doc(collection(db, `${root(APP_ID)}/pastDrafts`), String(year))
    : doc(collection(db, `${leagueRoot(leagueId)}/archives`), String(year));

export const archivesCollection = (leagueId: string = DEFAULT_LEAGUE_ID) =>
  isLegacy(leagueId)
    ? collection(db, `${root(APP_ID)}/pastDrafts`)
    : collection(db, `${leagueRoot(leagueId)}/archives`);

/** Registry of public leagues (id + display name) so the join screen can list them. */
export const leagueRegistryRef = (leagueId: string): DocumentReference =>
  doc(collection(db, `${root(APP_ID)}/leagueRegistry`), leagueId);

export const leagueRegistryCollection = () => collection(db, `${root(APP_ID)}/leagueRegistry`);

export async function ensureAuth(): Promise<string> {
  const cred = await signInAnonymously(auth);
  return cred.user.uid;
}

export {
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  collection,
};

export type { DraftState, ArchivedDraft };
